/**
 * components/landing/home/WhyBrand.jsx — Section 10.
 *
 * A comparison layout that pits the without-Kalki Ai Cloud / with-Kalki Ai Cloud paths
 * side by side.  Uses two contrasting columns — red-tinged "Without"
 * on the left, orange/green-tinged "With" on the right — both rendered
 * as glass cards.
 */

import clsx from 'clsx'
import { Container, Eyebrow, SectionHeading } from '../design-system'
import { X, CheckCircle2 } from 'lucide-react'

const WITHOUT = [
  'Months of platform development',
  'Large in-house engineering team',
  'Cloud + infra costs',
  'Maintenance + on-call rotation',
  'Security + compliance from scratch',
  'Constant framework updates',
  'AI provider integrations to hand-roll',
  'Trading engine, matching + risk',
  'Billing, subscriptions, invoices',
]

const WITH = [
  'Production-ready platform on day one',
  'Continuous improvements shipped to every tenant',
  'Enterprise multi-region architecture',
  'AI Mentor + Auto-Docs integrated',
  'Trading engine, risk & scanner included',
  'One-click tenant provisioning',
  'Stripe + NowPayments billing',
  'Documentation + governance built-in',
  'Technical support from the platform team',
]

const WhyBrand = () => (
  <section className="py-24 md:py-40">
    <Container>
      <SectionHeading
        eyebrow="Why Kalki Ai Cloud"
        title={<>Stop rebuilding the wheel.<br /><span className="text-zinc-400">Buy the vehicle.</span></>}
        subtitle="The build-vs-buy math for a modern AI trading platform is brutal.  Kalki Ai Cloud turns 24 months of engineering into an afternoon of configuration."
        align="center"
        className="items-center mx-auto"
      />

      <div className="mt-14 grid gap-4 md:grid-cols-2 max-w-5xl mx-auto">
        <div className="relative rounded-3xl border border-red-500/15 bg-red-500/[0.03] backdrop-blur-xl p-6 md:p-8 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-70" style={{ background: 'radial-gradient(40% 30% at 0% 0%, rgba(239,68,68,0.08), transparent 70%)' }} />
          <Eyebrow tone="neutral" className="!border-red-500/30 !bg-red-500/10 !text-red-300">Without Kalki Ai Cloud</Eyebrow>
          <div className="mt-4 text-xl md:text-2xl font-semibold text-white tracking-tight">The build-from-scratch path</div>
          <ul className="mt-5 space-y-2">
            {WITHOUT.map((w) => (
              <li key={w} className="flex items-start gap-2.5 text-[13px] text-zinc-300">
                <X className="h-4 w-4 mt-0.5 text-red-400/80 shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 rounded-xl border border-red-500/15 bg-red-500/[0.05] p-3 text-[11px] text-red-200/90">
            Typical timeline: <span className="font-semibold text-white">18–24 months</span> · team size: <span className="font-semibold text-white">15–25 engineers</span>.
          </div>
        </div>

        <div className="relative rounded-3xl border border-brand-gold/25 bg-brand-gold/[0.04] backdrop-blur-xl p-6 md:p-8 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-70" style={{ background: 'radial-gradient(40% 30% at 100% 100%, rgba(255,193,7,0.10), transparent 70%), radial-gradient(30% 40% at 0% 100%, rgba(16,185,129,0.06), transparent 70%)' }} />
          <Eyebrow>With Kalki Ai Cloud</Eyebrow>
          <div className="mt-4 text-xl md:text-2xl font-semibold text-white tracking-tight">The Kalki Ai Cloud path</div>
          <ul className="mt-5 space-y-2">
            {WITH.map((w) => (
              <li key={w} className="flex items-start gap-2.5 text-[13px] text-zinc-100">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-brand-emerald shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 rounded-xl border border-brand-gold/25 bg-brand-gold/[0.08] p-3 text-[11px] text-brand-gold">
            Typical timeline: <span className="font-semibold text-white">2–4 weeks</span> · team size: <span className="font-semibold text-white">1–2 people</span>.
          </div>
        </div>
      </div>
    </Container>
  </section>
)

export default WhyBrand
